// FeedCard.js
import React from 'react';
import { Button, Card, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import { onActivate } from '../../utils/external';
import './FeedItem.css';
import './FeedCard.css';

/**
 * Feed-card building blocks
 *
 * The pieces pages use to look like the feed without being a feed item:
 * the party page, the spending page and the company page.
 *
 * Each card is .feed-item around a react-bootstrap Card, with FeedItem.css
 * loaded, so it takes the feed's surface, shadow, radius, fonts and button
 * look in every theme without restating them. Footer buttons carry the feed's
 * own ids (#billLink for the first, #viewBillButton for the rest), which is
 * what theme.css styles.
 *
 * The title and body deliberately do NOT carry #billText and #billBodyText.
 * Rules against those ids are sized for a 160px carousel card, and an id
 * outranks any class: they stripped the title's padding, shrank it into the
 * card's rounded corner and drew a second surface behind the body. The
 * .card-title and .card-body classes still give both the feed's theme colors.
 */

export const FeedCard = ({ title, onTitleClick, footer, children, className = '' }) => (
  <div className={`feed-item feed-item--fullwidth feed-card ${className} mb-4`}>
    <Card>
      {title && (
        <Card.Title
          className={`card-title feed-card-title ${onTitleClick ? 'feed-card-title--link' : ''}`}
          onClick={onTitleClick}
          role={onTitleClick ? 'link' : undefined}
          tabIndex={onTitleClick ? 0 : undefined}
          onKeyDown={onTitleClick ? onActivate(onTitleClick) : undefined}
        >
          {title}
        </Card.Title>
      )}
      {children && <Card.Body className="feed-card-body">{children}</Card.Body>}
      {footer && <Card.Footer className="d-flex flex-wrap feed-card-footer">{footer}</Card.Footer>}
    </Card>
  </div>
);

export const FeedButton = ({ primary = false, onClick, children, className = '', ...rest }) => (
  <Button
    className={`clickable me-2 ${className}`}
    id={primary ? 'billLink' : 'viewBillButton'}
    onClick={onClick}
    {...rest}
  >
    {children}
  </Button>
);

// A row of feed buttons acting as tabs. Wrapped in .feed-item so the buttons
// take the feed look when they sit outside a card.
export const FeedSegments = ({ options, value, onChange, label, className = '' }) => (
  <div className={`feed-item feed-segments ${className}`} role="tablist" aria-label={label}>
    {options.map((option) => (
      <Button
        key={option.id}
        type="button"
        role="tab"
        aria-selected={value === option.id}
        className={`clickable feed-segment ${value === option.id ? 'feed-segment--active' : ''}`}
        onClick={() => onChange(option.id)}
      >
        {option.label}
      </Button>
    ))}
  </div>
);

// A heading above its card or cards, as a topic heading sits above its feed.
export const FeedSection = ({ title, subtitle, aside, children, id }) => (
  <section className="feed-section" id={id}>
    <div className="feed-section-head">
      <div className="feed-section-heading">
        <h2 className="feed-section-title">{title}</h2>
        {subtitle && <p className="feed-section-subtitle">{subtitle}</p>}
      </div>
      {aside}
    </div>
    {children}
  </section>
);

// Loading, failed and empty, said the same way everywhere. A 502 or 503 is
// the upstream source or a dataset not loaded yet, so it asks for a retry
// rather than claiming there is nothing; the API's own sentence is shown
// where it gave one.
export const PanelState = ({ loading, error, empty, emptyText }) => {
  const { t } = useTranslation('common');

  if (loading) {
    return (
      <div className="feed-panel-state">
        <Spinner animation="border" size="sm" />
      </div>
    );
  }

  if (error) {
    return (
      <p className="feed-panel-state feed-panel-state--text">
        {error.upstream
          ? t('common:sourceSlow', 'The source did not answer in time. Try again in a minute.')
          : error.detail || t('common:panelLoadError', 'This could not be loaded right now.')}
      </p>
    );
  }

  if (empty) {
    return (
      <p className="feed-panel-state feed-panel-state--text">
        {emptyText || t('common:nothingRecorded', 'Nothing recorded.')}
      </p>
    );
  }

  return null;
};

/**
 * LoadMore
 *
 * The button under a paged list. A spinner in the button while the next page
 * loads; gone when there is no next page, so it never invites a dead press.
 * A failed page keeps the rows already shown and says so above the button,
 * which then retries.
 */
export const LoadMore = ({ hasMore, loading, error, onClick, label }) => {
  const { t } = useTranslation('common');
  if (!hasMore && !error) return null;

  return (
    <div className="feed-item feed-load-more">
      {error && (
        <p className="feed-panel-state feed-panel-state--text">
          {error.upstream
            ? t('common:sourceSlow', 'The source did not answer in time. Try again in a minute.')
            : error.detail || t('common:panelLoadError', 'This could not be loaded right now.')}
        </p>
      )}
      <FeedButton primary onClick={onClick} disabled={loading}>
        {loading && <Spinner animation="border" size="sm" className="me-2" />}
        {label || t('common:loadMore', 'Load more')}
      </FeedButton>
    </div>
  );
};

/**
 * Bars
 *
 * A ranked list with a bar for each row's share. The bar is the share when
 * the row has one, otherwise the row's value against the largest in the
 * list. Negative values -- loan subsidy re-estimates -- get no bar rather
 * than a bar pointing the wrong way; the signed figure is printed.
 *
 * tone picks one color per direction, never red or green, because none of the
 * measures is a verdict: gov (money from the government), dep (dependence on
 * it), inf (spending to influence it), kind (a second division of a total).
 */
export const Bars = ({
  rows,
  value = (row) => row.amount,
  format,
  name = (row) => row.name,
  sub,
  share = (row) => row.share,
  tone = 'gov',
  onSelect,
  max,
}) => {
  const values = rows.map((row) => Number(value(row)) || 0);
  const largest = max || Math.max(...values.map((v) => Math.abs(v)), 1);

  return (
    <ul className={`feed-bars feed-bars--${tone}`}>
      {rows.map((row, index) => {
        const raw = value(row);
        const rowShare = typeof share(row) === 'number' ? share(row) : null;
        const width =
          raw < 0
            ? 0
            : Math.max(0.5, Math.min(100, rowShare !== null ? rowShare * 100 : (raw / largest) * 100));
        const label = name(row);
        const detail = sub ? sub(row) : null;
        const selectable = Boolean(onSelect);

        const content = (
          <>
            <div className="feed-bar-row">
              <span className="feed-bar-name">{label}</span>
              <span className="feed-bar-value">{format ? format(raw, row) : raw}</span>
            </div>
            <div className="feed-bar-track" aria-hidden>
              <span className="feed-bar-fill" style={{ width: `${width}%` }} />
            </div>
            {detail && <div className="feed-bar-sub">{detail}</div>}
          </>
        );

        return (
          <li key={`${label}-${index}`} className="feed-bar">
            {selectable ? (
              <button type="button" className="feed-bar-button" onClick={() => onSelect(row)}>
                {content}
              </button>
            ) : (
              content
            )}
          </li>
        );
      })}
    </ul>
  );
};